/** Internal: keyboard + mouse state. Use InputManager only. */

import type { KeyboardState, MouseState, MousePosition, Vector2Like } from "./Input_Types";
import { KeyCode, MouseButton } from "./Input_Types";

export class KeyboardMouseManager {
    // Keyboard state
    private keyboardState: KeyboardState = {
        pressedKeys: new Set(),
        lastPressedKey: null,
    };

    // Track previous frame's key states for one-frame press detection
    private previousPressedKeys: Set<string> = new Set();

    // Mouse state
    private mouseState: MouseState = {
        position: {
            screen: { x: 0, y: 0 },
            canvas: { x: 0, y: 0 },
            normalized: { x: 0, y: 0 },
        },
        pressedButtons: new Set(),
        wheelDelta: { x: 0, y: 0 },
        isOverCanvas: false,
        delta: { x: 0, y: 0 },
        velocity: { x: 0, y: 0 },
    };

    // Track previous frame's mouse button states for one-frame press detection
    private previousPressedButtons: Set<MouseButton> = new Set();

    private lastMousePosition: Vector2Like = { x: 0, y: 0 };
    private wheelDeltaConsumed: boolean = true;
    private dragStartPosition: MousePosition | null = null;
    private isDragging: boolean = false;
    private pointerLockDelta: Vector2Like = { x: 0, y: 0 }; // For pointer lock support

    /** Call every frame; optional deltaTime for velocity. */
    update(deltaTime?: number): void {
        // Check if pointer lock is active
        const isPointerLocked = document.pointerLockElement !== null;

        if (isPointerLocked) {
            // Use pointer lock delta (set by handlePointerLockMove)
            this.mouseState.delta = { ...this.pointerLockDelta };

            // Reset pointer lock delta for next frame
            this.pointerLockDelta = { x: 0, y: 0 };
        } else {
            // Normal mouse delta calculation
            const currentX = this.mouseState.position.screen.x;
            const currentY = this.mouseState.position.screen.y;

            this.mouseState.delta = {
                x: currentX - this.lastMousePosition.x,
                y: currentY - this.lastMousePosition.y,
            };

            this.lastMousePosition = { x: currentX, y: currentY };
        }

        // Calculate velocity
        if (deltaTime && deltaTime > 0) {
            this.mouseState.velocity = {
                x: this.mouseState.delta.x / deltaTime,
                y: this.mouseState.delta.y / deltaTime,
            };
        }

        // Reset wheel delta if consumed
        if (!this.wheelDeltaConsumed) {
            this.wheelDeltaConsumed = true;
        } else {
            this.mouseState.wheelDelta = { x: 0, y: 0 };
        }

        // Update previous key states for next frame (for one-frame press detection)
        this.previousPressedKeys.clear();
        this.keyboardState.pressedKeys.forEach(key => {
            this.previousPressedKeys.add(key);
        });

        // Update previous mouse button states for next frame (for one-frame press detection)
        this.previousPressedButtons.clear();
        this.mouseState.pressedButtons.forEach(button => {
            this.previousPressedButtons.add(button);
        });
    }

    // ===== KEYBOARD METHODS =====

    handleKeyDown(keyCode: string): void {
        this.keyboardState.pressedKeys.add(keyCode);
        this.keyboardState.lastPressedKey = keyCode;
    }

    handleKeyUp(keyCode: string): void {
        this.keyboardState.pressedKeys.delete(keyCode);
    }

    isKeyDown(key: string): boolean {
        return this.keyboardState.pressedKeys.has(key);
    }

    isKeyUp(key: string): boolean {
        return !this.keyboardState.pressedKeys.has(key);
    }

    /** True only on the frame key went down (one-shot, not held). */
    isKeyPressed(key: string): boolean {
        return this.keyboardState.pressedKeys.has(key) && !this.previousPressedKeys.has(key);
    }

    isAnyKeyDown(keys: string[]): boolean {
        return keys.some((key) => this.keyboardState.pressedKeys.has(key));
    }

    areAllKeysDown(keys: string[]): boolean {
        return keys.every((key) => this.keyboardState.pressedKeys.has(key));
    }

    getMovementVector(): Vector2Like {
        const movement = { x: 0, y: 0 };

        if (this.isKeyDown(KeyCode.A) || this.isKeyDown(KeyCode.ARROW_LEFT)) movement.x -= 1;
        if (this.isKeyDown(KeyCode.D) || this.isKeyDown(KeyCode.ARROW_RIGHT)) movement.x += 1;
        if (this.isKeyDown(KeyCode.W) || this.isKeyDown(KeyCode.ARROW_UP)) movement.y += 1;
        if (this.isKeyDown(KeyCode.S) || this.isKeyDown(KeyCode.ARROW_DOWN)) movement.y -= 1;

        return movement;
    }

    getKeyboardState(): Readonly<KeyboardState> {
        return { ...this.keyboardState };
    }

    getPressedKeys(): string[] {
        return Array.from(this.keyboardState.pressedKeys);
    }

    getLastPressedKey(): string | null {
        return this.keyboardState.lastPressedKey;
    }

    // ===== MOUSE METHODS =====

    setMousePosition(position: MousePosition): void {
        this.mouseState.position = position;
    }

    handleMouseButtonDown(button: number): void {
        if (button >= 0 && button <= 2) {
            this.mouseState.pressedButtons.add(button as MouseButton);
        }
        this.dragStartPosition = { ...this.mouseState.position };
        this.isDragging = false;
    }

    handleMouseButtonUp(button: number): void {
        this.mouseState.pressedButtons.delete(button as MouseButton);
        if (this.mouseState.pressedButtons.size === 0) {
            this.isDragging = false;
            this.dragStartPosition = null;
        }
    }

    handleMouseMove(): void {
        if (this.mouseState.pressedButtons.size > 0 && this.dragStartPosition) {
            this.isDragging = true;
        }
    }

    /** Accumulate pointer-lock movement (movementX/movementY). */
    handlePointerLockMove(movementX: number, movementY: number): void {
        this.pointerLockDelta.x += movementX;
        this.pointerLockDelta.y += movementY;
    }

    handleMouseWheel(deltaX: number, deltaY: number): void {
        this.mouseState.wheelDelta = { x: deltaX, y: deltaY };
        this.wheelDeltaConsumed = false;
    }

    setMouseOverCanvas(isOver: boolean): void {
        this.mouseState.isOverCanvas = isOver;
        if (!isOver) {
            this.isDragging = false;
            this.dragStartPosition = null;
        }
    }

    isMouseButtonDown(button: MouseButton): boolean {
        return this.mouseState.pressedButtons.has(button);
    }

    /** True only on the frame button went down (one-shot, not held). */
    isMouseButtonPressed(button: MouseButton): boolean {
        return this.mouseState.pressedButtons.has(button) && !this.previousPressedButtons.has(button);
    }

    getMousePosition(): MousePosition {
        return { ...this.mouseState.position };
    }

    getMouseWheelDelta(): Vector2Like {
        return { ...this.mouseState.wheelDelta };
    }

    isMouseOverCanvas(): boolean {
        return this.mouseState.isOverCanvas;
    }

    getMouseState(): Readonly<MouseState> {
        return { ...this.mouseState };
    }

    getMouseDelta(): Vector2Like {
        return { ...this.mouseState.delta };
    }

    getMouseVelocity(): Vector2Like {
        return { ...this.mouseState.velocity };
    }

    isMouseDragging(button?: MouseButton): boolean {
        if (button !== undefined) {
            return this.isDragging && this.mouseState.pressedButtons.has(button);
        }
        return this.isDragging;
    }

    getDragStartPosition(): MousePosition | null {
        return this.dragStartPosition ? { ...this.dragStartPosition } : null;
    }

    getDragDelta(): Vector2Like {
        if (!this.dragStartPosition) {
            return { x: 0, y: 0 };
        }

        return {
            x: this.mouseState.position.screen.x - this.dragStartPosition.screen.x,
            y: this.mouseState.position.screen.y - this.dragStartPosition.screen.y,
        };
    }

    // ===== UTILITY =====

    clear(): void {
        this.keyboardState.pressedKeys.clear();
        this.keyboardState.lastPressedKey = null;
        this.previousPressedKeys.clear();
        this.mouseState.pressedButtons.clear();
        this.previousPressedButtons.clear();
        this.dragStartPosition = null;
        this.isDragging = false;
    }
}

